/**
 * Utility functions for lot status and reserve price checks
 */

/**
 * Check if a lot is closed
 * @param {Object} lot - The lot object
 * @returns {boolean} True if lot status is 'closed', 'cancelled', or 'timeout'
 */
export const checkIfLotClosed = (lot) => {
    return lot.status === 'closed' || lot.status === 'cancelled' || lot.status === 'timeout';
};

/**
 * Check if a lot is effectively cancelled (closed without reaching reserve price)
 * @param {Object} lot - The lot object
 * @returns {boolean} True if lot is cancelled or closed without meeting reserve price
 */
export const isLotCancelled = (lot) => {
    // If status is explicitly cancelled, return true
    if (lot.status === 'cancelled') {
        return true;
    }
    
    // If status is closed and lot has a reserve price, check if reserve was met
    if (lot.status === 'closed' && lot.is_reserve && lot.reserve_price) {
        const currentPrice = parseFloat(lot.current_price || lot.starting_price || 0);
        const reservePrice = parseFloat(lot.reserve_price || 0);
        
        // Only consider cancelled if reserve price exists and current price is less than reserve
        // Reserve is met when current price >= reserve price
        if (reservePrice > 0 && currentPrice > 0) {
            return currentPrice < reservePrice;
        }
    }
    
    return false;
};

/**
 * Check if current price is 90% or more of reserve price
 * @param {Object} lot - The lot object
 * @returns {boolean} True if current price is 90% or more of reserve price but still below it
 */
export const isCloseToReserve = (lot) => {
    // Check if lot has a reserve price
    if (!lot.reserve_price || lot.reserve_price === null || lot.reserve_price === undefined) {
        return false;
    }
    const currentPrice = parseFloat(lot.current_price || lot.starting_price);
    const reservePrice = parseFloat(lot.reserve_price);
    if (reservePrice <= 0 || currentPrice <= 0 || isNaN(currentPrice) || isNaN(reservePrice)) {
        return false;
    }
    const percentage = (currentPrice / reservePrice) * 100;
    // Show indicator when price is 90% or more of reserve price, but still below it
    const isClose = percentage >= 90 && currentPrice < reservePrice;
    
    // Debug logging (can be removed later)
    if (lot.reserve_price && lot.status === 'active') {
        console.log('[Reserve Debug]', {
            lotId: lot.id,
            lotNumber: lot.lot_number,
            currentPrice,
            reservePrice,
            percentage: percentage.toFixed(2),
            isClose,
            hasReserve: !!lot.reserve_price
        });
    }
    
    return isClose;
};

/**
 * Check if reserve price is met or exceeded
 * @param {Object} lot - The lot object
 * @returns {boolean} True if current price is greater than or equal to reserve price
 */
export const isReserveMet = (lot) => {
    if (!lot.reserve_price || lot.reserve_price === null || lot.reserve_price === undefined) {
        return false;
    }
    const currentPrice = parseFloat(lot.current_price || lot.starting_price);
    const reservePrice = parseFloat(lot.reserve_price);
    if (reservePrice <= 0 || currentPrice <= 0 || isNaN(currentPrice) || isNaN(reservePrice)) {
        return false;
    }
    // Reserve is met when current price is greater than or equal to reserve price
    return currentPrice >= reservePrice;
};


export const announcementSounds = [
    { value: 'bool_lagae.mp3', label: 'بول لگائیں (Bool Lagae)' },
    { value: 'dekhe_bol lagae.mp3', label: 'دیکھے بول لگائیں (Dekhe Bol Lagae)' },
    { value: 'lar_raha_ha.mp3', label: 'لڑ رہا ہے (Lar Raha Ha)' },
    { value: 'mubarak.mp3', label: 'مبارک (Mubarak)' },
    { value: 'nayi_halat.mp3', label: 'نئی حالت (Nayi Halat)' },
    { value: 'pichay_wale_sahab.mp3', label: 'پیچھے والے صاحب (Pichay Wale Sahib)' },
    { value: 'rate.mp3', label: 'ریٹ (Rate)' },
    { value: 'agli_chize.mp3', label: 'اگلی چیزیں (Agli Chize)' },
    { value: 'hath_me_moqa_ha.mp3', label: 'ہاتھ میں موقع ہے (Hath Mein Moqa Hai)' },
    { value: 'yeh_chize_tumare_baghair_nahe.mp3', label: 'یہ چیزیں تمہارے بغیر نہیں (Yeh Chize Tumhare Baghair Nahi)' },
    { value: 'boli_lagao_warna_agay_saal_tak_hasrat.mp3', label: 'بولی لگاؤ ورنہ اگلے سال تک حسرت (Boli Lagao Warna Agay Saal Tak Hasrat)' },
    { value: 'ek_kadam_agay.mp3', label: 'ایک قدم آگے (Ek Kadam Agay)' },
    { value: 'akri_lamha_bari_boli_ka_waqt.mp3', label: 'آخری لمحہ بڑی بولی کا وقت (Akri Lamha Bari Boli Ka Waqt)' },
    { value: 'need_nahe_ahay_gi.mp3', label: 'نہیں آئے گی (Need Nahe Ahay Gi)' },
    { value: 'humsaya_le_jaye_ga.mp3', label: 'ہمسایہ لے جائے گا (Humsaya Le Jaye Ga)' },
    { value: 'duniya_me_har_chize.mp3', label: 'دنیا میں ہر چیز (Duniya Me Har Chize)' },
    { value: 'humari_gari_jeetnay_walo_ke_saat.mp3', label: 'ہماری گاڑی جیتنے والوں کے ساتھ (Humari Gari Jeetnay Walo Ke Saat)' },
    { value: 'agli_boli_app_se_hi.mp3', label: 'اگلی بولی آپ سے ہی (Agli Boli App Se Hi)' },
    { value: 'thori_si_boli.mp3', label: 'تھوڑی سی بولی (Thori Si Boli)' },
    { value: 'ghar_wali.mp3', label: 'گھر والی (Ghar Wali)' },
    { value: 'me_apne_liye_rak_luga.mp3', label: 'میں اپنے لیے رکھ لوں گا (Me Apne Liye Rak Luga)' },
    { value: 'akri_moka.mp3', label: 'آخری موقع (Akri Moka)' },
    { value: 'bacho_ki_jaab_kharch.mp3', label: 'بچوں کی جیب خرچ (Bacho Ki Jaab Kharch)' },
    { value: 'yeh_app_ke_budget.mp3', label: 'یہ آپ کے بجٹ (Yeh App Ke Budget)' },
    { value: 'dil_jeet_liya.mp3', label: 'دل جیت لیا (Dil Jeet Liya)' },
    { value: 'yeh_lot_apna_bana_ke_jaye.mp3', label: 'یہ لات اپنا بنا کے جائے (Yeh Lot Apna Bana Ke Jaye)' },
    { value: 'fixed_price_laga_di.mp3', label: 'فکسڈ پرائس لگائیں (Fixed Price Laga Di)' }
];